import { useEffect } from "react";
import * as yup from "yup";
import { useHistory, useLocation } from "react-router-dom";

declare global {
  interface Window {
    // Set by index.html before analytics loads. Never persist the return secret.
    __moovsPaymentSetupReturn?: { pathname: string; search: string };
  }
}

const RETURN_KEYS = [
  "setup_intent",
  "setup_intent_client_secret",
  "redirect_status",
];

export const removeSetupReturnParams = (search: string) => {
  const params = new URLSearchParams(search);
  RETURN_KEYS.forEach((key) => params.delete(key));
  const remaining = params.toString();
  return remaining ? `?${remaining}` : "";
};

/** Also handles client-side navigation and hosts that bypass index.html. */
export const readSetupReturnParams = (pathname: string, search: string) => {
  const params = new URLSearchParams(search);
  if (RETURN_KEYS.some((key) => params.has(key))) {
    const captured = new URLSearchParams();
    RETURN_KEYS.forEach((key) => {
      if (params.has(key)) captured.set(key, params.get(key));
    });
    window.__moovsPaymentSetupReturn = {
      pathname,
      search: captured.toString(),
    };
    if (
      window.location.pathname === pathname &&
      window.location.search === search
    ) {
      window.history.replaceState(
        window.history.state,
        "",
        `${pathname}${removeSetupReturnParams(search)}${window.location.hash}`
      );
    }
  }
  const captured = window.__moovsPaymentSetupReturn;
  return new URLSearchParams(
    captured?.pathname === pathname ? captured.search : ""
  );
};

export const clearSetupReturnParams = (setupIntentId?: string) => {
  const captured = window.__moovsPaymentSetupReturn;
  if (
    !setupIntentId ||
    new URLSearchParams(captured?.search).get("setup_intent") === setupIntentId
  ) {
    delete window.__moovsPaymentSetupReturn;
  }
};

/** Keep the router in sync even when correlation is invalid or saving fails. */
export const useSetupReturnUrlCleanup = () => {
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const cleaned = removeSetupReturnParams(location.search);
    if (cleaned !== location.search) {
      readSetupReturnParams(location.pathname, location.search);
      history.replace({ ...location, search: cleaned });
    }
  }, [history, location]);
};

// Flow-specific schemas extend this correlation; owner/source checks stay in their adapters.
export const setupReturnCorrelationSchema = yup.object({
  returnPath: yup.string().required(),
  setupIntentId: yup
    .string()
    .matches(/^seti_[A-Za-z0-9]+$/)
    .required(),
});

const readSetupCorrelation = <S extends yup.AnyObjectSchema>(
  storageKey: string,
  schema: S
): yup.InferType<S> | null => {
  try {
    const stored = sessionStorage.getItem(storageKey);
    return stored
      ? schema.validateSync(JSON.parse(stored), { stripUnknown: true })
      : null;
  } catch {
    return null;
  }
};

export const readCorrelatedSetupReturn = <S extends yup.AnyObjectSchema>(
  storageKey: string,
  schema: S,
  pathname = window.location.pathname,
  search = window.location.search
): (yup.InferType<S> & { clientSecret: string }) | null => {
  const params = readSetupReturnParams(pathname, search);
  const correlation = readSetupCorrelation(storageKey, schema);
  const clientSecret = params.get("setup_intent_client_secret");
  if (
    !correlation ||
    correlation.returnPath !== pathname ||
    correlation.setupIntentId !== params.get("setup_intent") ||
    !clientSecret?.startsWith(`${correlation.setupIntentId}_secret_`)
  )
    return null;
  return { ...correlation, clientSecret };
};

export const clearCorrelatedSetupReturn = (
  storageKey: string,
  schema: yup.AnyObjectSchema,
  setupIntentId: string
) => {
  clearSetupReturnParams(setupIntentId);
  if (readSetupCorrelation(storageKey, schema)?.setupIntentId === setupIntentId)
    sessionStorage.removeItem(storageKey);
};
